import React, { useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18next';
import './login.css';
import LogoFormContainer from './LogoFormContainer';
import {
  MButton,
  BufferService,
  LoadingContext,
  DefaultFunctionURL,
} from 'src/M_Components';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';

// Auth providers + storage keys
import { useUserAuth } from 'src/components/auth/UserAuthProvider';
import { useCarrierAuth } from 'src/components/auth/CarrierAuthProvider';
import { STORAGE_USER, STORAGE_CARRIER } from 'src/components/auth/storageKeys';

//#region Constants

/**
 * Key used in localStorage to persist the pending invite context
 * created by CarrierInviteEntryPage.
 *
 * Shape example:
 * {
 *   type: "spot" | "phase",
 *   tenderId: number | null,
 *   tenderPhaseId: number | null,
 *   token: string,
 *   createdAt: number
 * }
 *
 * The actual consumption of this context is handled inside the carrier realm shell.
 */
const PENDING_INVITE_KEY = 'carrier_invite_pending';

/**
 * Internal carrier invite route used after a successful carrier login
 * when a pending invite context exists.
 */
const INVITE_TARGET_ROUTE = '/carrier/home/CarrierInviteViewBid';

//#endregion

//#region Component

/**
 * Login component.
 *
 * Supports two authentication realms:
 * - user;
 * - carrier.
 *
 * User realm:
 * - calls /api/Identity/login;
 * - stores user session data;
 * - redirects to #/home.
 *
 * Carrier realm:
 * - calls /api/CarrierIdentity/login;
 * - stores carrier session data;
 * - redirects to #/carrier/home or to the invite wrapper when a pending invite exists.
 *
 * @param {object} props Component props.
 * @param {'user'|'carrier'} props.realm Authentication realm.
 * @returns {JSX.Element} Login page.
 */
export default function Login({ realm = 'user' }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);

  const userAuth = useUserAuth();
  const carrierAuth = useCarrierAuth();

  const [emailOrUserName, setEmailOrUserName] = useState('');
  const [password, setPassword] = useState('');

  const baseUrl =
    DefaultFunctionURL?.() || process.env.REACT_APP_API_BASE_URL || '';

  /**
   * Login service resolved by realm.
   */
  const loginService = useMemo(
    () =>
      new BufferService(
        realm === 'carrier' ? 'CarrierIdentity/login' : 'Identity/login',
        baseUrl
      ),
    [realm, baseUrl]
  );

  //#region Private helpers

  /**
   * Shows a normalized API error toast using the shared API helpers.
   *
   * @param {any} payload API payload or thrown error.
   * @param {string} fallback Fallback message when the API does not provide one.
   */
  const showApiError = (payload, fallback = t('LabelTryAgain')) => {
    ctx.toastMessages('error', '', apiMessage(payload, fallback));
  };

  /**
   * Persists the user authentication payload into storage.
   *
   * @param {object} response Successful user login payload.
   */
  const persistUserSession = (response) => {
    try {
      localStorage.setItem(STORAGE_USER.token, response.Token);
      localStorage.setItem(
        STORAGE_USER.menus,
        JSON.stringify(response.Menus || [])
      );
      localStorage.setItem(
        STORAGE_USER.permissions,
        JSON.stringify(response.Permissions || [])
      );

      // Legacy keys preserved for compatibility with existing screens
      localStorage.setItem('permissions', JSON.stringify(response.Permissions || []));
      localStorage.setItem('defaultAccountName', response.DefaultAccountName || '');
      localStorage.setItem('token', response.Token || '');
      localStorage.setItem('defaultShipperId', String(response.DefaultShipperId ?? ''));
      localStorage.setItem('defaultShipperName', response.DefaultShipperName ?? '');
      localStorage.setItem('userId', String(response.Id ?? ''));
      localStorage.setItem('cargofiveApiKeyId', String(response.CargofiveApiKeyId ?? ''));

      localStorage.setItem(
        STORAGE_USER.profile,
        JSON.stringify({
          id: response.Id,
          userName: response.UserName,
          companyCode: response.CompanyCode,
          companyName: response.CompanyName,
          defaultAccountName: response.DefaultAccountName,
          defaultShipperId: response.DefaultShipperId,
          defaultShipperName: response.DefaultShipperName,
          cargofiveApiKeyId: response.CargofiveApiKeyId,
        })
      );
    } catch {
      // Storage failures are intentionally ignored so in-memory auth can still proceed.
    }
  };

  /**
   * Persists the carrier authentication payload into storage.
   *
   * @param {object} response Successful carrier login payload.
   */
  const persistCarrierSession = (response) => {
    try {
      localStorage.setItem(STORAGE_CARRIER.token, response.Token);

      if (response.ExpiresUtc) {
        localStorage.setItem(STORAGE_CARRIER.expiresUtc, response.ExpiresUtc);
      }

      localStorage.setItem(
        STORAGE_CARRIER.menus,
        JSON.stringify(response.Menus || [])
      );
      localStorage.setItem(
        STORAGE_CARRIER.permissions,
        JSON.stringify(response.Permissions || [])
      );
      localStorage.setItem(
        STORAGE_CARRIER.session,
        JSON.stringify(response.Session || null)
      );
      localStorage.setItem(
        STORAGE_CARRIER.profile,
        JSON.stringify({
          account: response.Account || null,
          carrier: response.Carrier || null,
          permissionsClaim: response.PermissionsClaim || null,
        })
      );
    } catch {
      // Storage failures are intentionally ignored so in-memory auth can still proceed.
    }
  };

  /**
   * Redirects a carrier after login.
   *
   * Behaviour:
   * - when a valid pending invite exists, redirects directly to the internal invite wrapper;
   * - otherwise redirects to the normal carrier home.
   *
   * The pending invite is intentionally not removed here.
   * Cleanup remains owned by the invite flow.
   */
  const redirectCarrierAfterLogin = () => {
    try {
      const raw = window.localStorage.getItem(PENDING_INVITE_KEY);

      if (raw) {
        const ctxInvite = JSON.parse(raw);

        const isValid =
          ctxInvite &&
          typeof ctxInvite.token === 'string' &&
          ctxInvite.token.length > 0 &&
          (ctxInvite.tenderId || ctxInvite.tenderPhaseId);

        if (isValid) {
          const searchParams = new URLSearchParams();

          if (ctxInvite.type) {
            searchParams.set('type', String(ctxInvite.type));
          }
          if (ctxInvite.tenderId) {
            searchParams.set('tenderId', String(ctxInvite.tenderId));
          }
          if (ctxInvite.tenderPhaseId) {
            searchParams.set('tenderPhaseId', String(ctxInvite.tenderPhaseId));
          }

          searchParams.set('token', String(ctxInvite.token));

          window.location.href =
            `#${INVITE_TARGET_ROUTE}?${searchParams.toString()}`;
          return;
        }
      }
    } catch {
      // Malformed storage content is ignored and the default route is used instead.
    }

    window.location.href = '#/carrier/home';
  };

  //#endregion

  //#region Event handlers

  /**
   * Handles the login submit flow for the active realm.
   *
   * Uses the normalized BufferService payload contract:
   * - success payloads are returned as regular objects with hidden success metadata;
   * - failure payloads are detected with isFailurePayload;
   * - API messages are extracted through apiMessage.
   */
  const handleLoginSubmit = async () => {
    if (!emailOrUserName || !password) {
      ctx.toastMessages('error', t('LabelTryAgain'), '');
      return;
    }

    const body = {
      username: emailOrUserName,
      password,
    };

    ctx.setIsLoad(true);

    try {
      const response = await loginService.post(body);

      if (isFailurePayload(response)) {
        showApiError(response, t('LabelTryAgain'));
        return;
      }

      //#region User realm

      if (realm === 'user') {
        /**
         * Existing functional rule:
         * when login succeeds but no token is returned,
         * the account still requires email confirmation.
         */
        if (!response?.Token) {
          ctx.toastMessages('success', '', t('LabelNeedConfirmEmail'));
          return;
        }

        persistUserSession(response);

        userAuth.login(response.Token);
        i18n.changeLanguage(localStorage.getItem('lng') || 'pt');

        ctx.toastMessages('success', '', t('AlertMessageSucessLogged'));
        window.location.href = '#/home';
        return;
      }

      //#endregion

      //#region Carrier realm

      if (!response?.Token) {
        showApiError(response, t('LabelTryAgain'));
        return;
      }

      persistCarrierSession(response);

      carrierAuth.login(response.Token, response.ExpiresUtc || null);
      i18n.changeLanguage(localStorage.getItem('lng') || 'pt');

      ctx.toastMessages('success', '', t('AlertMessageSucessLogged'));
      redirectCarrierAfterLogin();

      //#endregion
    } catch (error) {
      showApiError(error, t('LabelTryAgain'));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /**
   * Submits login on Enter key press.
   *
   * @param {KeyboardEvent} e Keyboard event.
   */
  const handleEnter = (e) => {
    if (e.key === 'Enter') {
      handleLoginSubmit();
    }
  };

  //#endregion

  //#region Render

  return (
    <div className="login-page">
      <div className="login-side-left">
        <img
          src="./images/Logo-devlop.png"
          alt="logoDevlop"
          className="devlop-logo"
        />
      </div>

      <div className="login-side-center">
        <LogoFormContainer
          handleLoginSubmit={handleLoginSubmit}
          handleEnter={handleEnter}
          setEmail={setEmailOrUserName}
          setPassword={setPassword}
          realm={realm}
          t={t}
          ctx={ctx}
        />
      </div>

      <div className="login-side-right">
        <div className="social-buttons-wrapper">
          {[
            {
              label: 'Devlop Facebook',
              link: 'https://www.facebook.com/devlop.systems',
              src: './icons/socialIcons/Facebook.svg',
            },
            {
              label: 'Devlop Instagram',
              link: 'https://www.instagram.com/devlop.systems/',
              src: './icons/socialIcons/Instagram.svg',
            },
            {
              label: 'Devlop Linkedin',
              link: 'https://www.linkedin.com/company/devlop-systems/mycompany/',
              src: './icons/socialIcons/Linkedin.svg',
            },
            {
              label: 'Devlop Youtube',
              link: 'https://www.youtube.com/@devlopsystems',
              src: './icons/socialIcons/Youtube.svg',
            },
          ].map((el) => (
            <div key={el.label} className="social-button-container">
              <MButton
                none
                backgroundColor="transparent"
                tooltip={el.label}
                icon={<img src={el.src} alt={el.label} />}
                onClick={() => window.open(el.link, '_blank')}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  //#endregion
}

//#endregion